import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { catchError, map, retry } from 'rxjs/operators'
import { CustomPaginationType } from '../../features/shared/custom-components/custom-pagination/modals/enum/custom-pagination.enum'
import { ClaimReason, CreditClaim } from '../../models/credit-claims.model'
import { SharedKey, SharedValue } from '../../models/enum/shared.enum'
import { ISharedPagination } from '../../models/interface/shared.interface'
import { AppConfigurationService } from '../app-configuration.service'

@Injectable({
  providedIn: 'root'
})
export class CreditClaimsService {
  constructor(
    private http: HttpClient,
    private appConfigurationService: AppConfigurationService
  ) {}

  // TODO AC-343 [implement pdf download]
  public getCreditClaimDetails(code: string): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/creditClaims/${code}`
    return this.http.get(url).pipe(retry(1))
  }

  public getCreditClaims(params?: ISharedPagination): Observable<any> {
    let payload: HttpParams = new HttpParams().set(SharedKey.pageSize, SharedValue.pageSize.toString())

    if (params?.pageNumber) {
      payload = payload.set(SharedKey.currentPage, (params.pageNumber - 1).toString())
    }
    if (params?.sort) {
      payload = payload.set(SharedKey.sort, params.sort)
    }
    if (params?.pageSize) {
      payload = payload.set(SharedKey.pageSize, params.pageSize.toString())
    }
    if (params?.status) {
      payload = payload.set(SharedKey.statuses, params.status)
    }
    if (params?.creditClaimCode) {
      payload = payload.set(SharedKey.creditClaimCode, params.creditClaimCode)
    }

    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/creditClaims`
    return this.http.get<any>(url, { params: payload }).pipe(
      map((res) => {
        res.pagination.type = CustomPaginationType.CUSTOM
        return res
      }),
      retry(1)
    )
  }

  public getCreditClaimDetail(creditClaimCode: string, queryParam?: any): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/creditClaims/${creditClaimCode}`
    return this.http.get<any>(url, { params: queryParam }).pipe(
      retry(1),
      catchError(() => {
        return of(null)
      })
    )
  }

  public getAgreementDetail(code: string): Observable<any> {
    const payload: HttpParams = new HttpParams().set(SharedKey.fields, SharedValue.full)

    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/agreement/${code}`
    return this.http.get<any>(url, { params: payload }).pipe(
      retry(1),
      catchError(() => {
        return of({})
      })
    )
  }

  public getAgreementDetailEntries(code: string, params: ISharedPagination): Observable<any> {
    let payload: HttpParams = new HttpParams()
      .set(SharedKey.fields, SharedValue.full)
      .set(SharedKey.pageSize, SharedValue.pageSize.toString())

    if (params?.pageNumber) {
      payload = payload.set(SharedKey.currentPage, params.pageNumber.toString())
    }

    const baseSite = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseSite}/users/current/agreement/${code}/entries`
    return this.http.get<any>(url, { params: payload }).pipe(
      map((res) => {
        res.pagination.type = CustomPaginationType.CUSTOM
        return res
      }),
      retry(1),
      catchError(() => {
        return of(null)
      })
    )
  }
  public getCreditClaimReasons(): Observable<ClaimReason[]> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/creditClaims/reasons`
    return this.http.get<any>(url).pipe(
      retry(1),
      catchError(() => {
        throw new Error(SharedValue.errorTryAgain)
      })
    )
  }

  public submitCreditClaim(creditClaim: CreditClaim): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/creditClaim`
    return this.http.post<any>(url, creditClaim).pipe(
      retry(1),
      catchError(() => {
        throw new Error(SharedValue.errorTryAgain)
      })
    )
  }
}
